import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Star, ExternalLink, Heart } from "lucide-react";
import { usePageMeta } from "../hooks/usePageMeta";

// Direct Google review-write URL for Shantanu Netralaya. Uses the real
// Place ID so a scan of the OPD QR-code goes single-hop straight to the
// Google review form — no manual search step for the patient.
const GOOGLE_REVIEW_URL =
  "https://search.google.com/local/writereview?placeid=ChIJqU-kb_YxjjkRr_8zU67l7Bg";
const GOOGLE_FALLBACK_URL =
  "https://www.google.com/maps?cid=1796062888296972207";

const REDIRECT_DELAY_MS = 1400;

/**
 * /review — one-tap Google-review deep-link.  Landing here shows a
 * calm thank-you screen for 1.4 s while auto-redirecting the patient
 * straight to Google's review-write form.
 *
 * Print this URL as a QR code for the OPD reception desk and hand it
 * to post-op patients — you should get 2-4 new Google reviews per
 * week which is the single biggest local-pack ranking lever.
 */
const Review = () => {
  usePageMeta({
    title: "Leave a Review · Shantanu Netralaya",
    path: "/review",
    description:
      "Share your experience with Shantanu Netralaya Eye Hospital, Varanasi. Redirects to Google review in a moment.",
    // We don't want this page indexed — its sole purpose is redirect.
    robots: "noindex, follow",
  });

  useEffect(() => {
    const t = setTimeout(() => {
      window.location.replace(GOOGLE_REVIEW_URL);
    }, REDIRECT_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="review-redirect-page">
      <div className="max-w-lg text-center">
        <div className="mx-auto w-16 h-16 rounded-full bg-[hsl(var(--accent))]/15 flex items-center justify-center">
          <Star size={28} strokeWidth={1.5} className="text-[hsl(var(--accent))]" />
        </div>
        <div className="overline mt-6 inline-flex items-center gap-2">
          <Heart size={13} /> Thank you
        </div>
        <h1 className="font-serif text-3xl sm:text-4xl mt-3 leading-tight">
          Redirecting you to Google to share your experience…
        </h1>
        <p className="mt-4 text-sm text-muted-foreground">
          Your honest review helps other families in Varanasi find good eye care.
          If the page doesn't open automatically, tap the button below.
        </p>
        <div className="mt-6 flex justify-center">
          <a
            href={GOOGLE_REVIEW_URL}
            className="btn-primary"
            data-testid="review-redirect-manual"
          >
            Open Google review <ExternalLink size={14} />
          </a>
        </div>
        <div className="mt-8 text-xs text-muted-foreground">
          <Link to="/" className="underline decoration-dotted underline-offset-4">← Back to Shantanu Netralaya</Link>
        </div>
      </div>
    </div>
  );
};

export default Review;
